/*
 * Behavioural FX events that need no page code: navigation (entry / internal /
 * exit), time_on_page (+ _count), scroll_depth, cta_click and the
 * abandon_checkout funnel event. One set of passive document listeners, one
 * state object per page view.
 */
import {trackFxEvent} from './optimizely-fx-events'
import {trackFxFunnelEvent} from './optimizely-fx-commerce'
import {classifyPage, getFxPageContext} from './optimizely-fx-page-context'

const SCROLL_MARKS = [25, 50, 75, 100]
const IDLE_MS = 30000
const CTA_SELECTOR = 'a, button, [role="button"], input[type="submit"]'
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart']

let started = false
let siteCountry = ''
let page = null
let idleTimer = null
let scrollFrame = null

const now = () => Date.now()
const visible = () => document.visibilityState !== 'hidden'

const pauseActive = () => {
    if (page?.activeSince != null) {
        page.activeMs += now() - page.activeSince
        page.activeSince = null
    }
}

const resumeActive = () => {
    if (page && page.activeSince == null && visible()) page.activeSince = now()
}

const activeSeconds = () => {
    if (!page) return 0
    const live = page.activeSince != null ? now() - page.activeSince : 0
    return Math.round((page.activeMs + live) / 1000)
}

const onActivity = () => {
    resumeActive()
    clearTimeout(idleTimer)
    idleTimer = setTimeout(pauseActive, IDLE_MS)
}

const onVisibility = () => (visible() ? onActivity() : pauseActive())

const stripQuery = (href) => {
    try {
        const url = new URL(href)
        return `${url.origin}${url.pathname}`
    } catch {
        return href
    }
}

/* Fired when a page view ends; the count is its denominator, so it follows. */
const sendTimeOnPage = () => {
    if (!page) return
    const seconds = activeSeconds()
    const {URL, Category, Subcategory, SKU} = page.context
    trackFxEvent(
        'time_on_page',
        {value: seconds},
        {URL, Category, Subcategory, SKU, duration_seconds: seconds}
    )
    trackFxEvent('time_on_page_count', null, {URL})
}

/* Reached checkout, then went anywhere but checkout or the confirmation. */
const sendAbandonCheckout = (leftPage, toType) => {
    if (leftPage?.context.page_type !== 'checkout') return
    if (toType === 'checkout' || toType === 'order_confirm') return
    trackFxFunnelEvent({
        id: 'abandon_checkout',
        state: 'abandoned',
        result: toType,
        URL: leftPage.context.URL
    })
}

const sendNavigation = ({from, to, navType, fromUrl, toUrl, external}) => {
    trackFxEvent('navigation', null, {
        URL: window.location.href,
        Text: `${from} > ${to}`,
        from_type: from,
        to_type: to,
        from_url: fromUrl,
        to_url: toUrl,
        nav_type: navType,
        external: !!external,
        country: siteCountry
    })
}

const checkScroll = () => {
    scrollFrame = null
    if (!page) return
    const doc = document.documentElement
    const total = Math.max(doc.scrollHeight, 1)
    const seen = Math.min(100, Math.round(((window.scrollY + window.innerHeight) / total) * 100))
    for (const mark of SCROLL_MARKS) {
        if (seen < mark || page.scrollMarks.has(mark)) continue
        page.scrollMarks.add(mark)
        const {URL, Category, Subcategory, page_type} = getFxPageContext(page.pathname)
        trackFxEvent('scroll_depth', null, {
            URL,
            Category,
            Subcategory,
            percentage: mark,
            content_state: page.contentState,
            page_type
        })
    }
}

const onScroll = () => {
    if (scrollFrame == null) scrollFrame = window.requestAnimationFrame(checkScroll)
}

const ctaLabel = (el) =>
    el.id ||
    el.dataset.fxCta ||
    el.dataset.testid ||
    el.getAttribute('aria-label') ||
    (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60)

const ctaState = (el) => {
    if (el.disabled) return 'disabled'
    return el.getAttribute('aria-expanded') ?? el.getAttribute('aria-pressed') ?? ''
}

const onClick = (event) => {
    const el = event.target?.closest?.(CTA_SELECTOR)
    if (!el || !page) return

    // `URL: pageUrl` — plain URL would shadow the global URL constructor below.
    const {URL: pageUrl, Category, Subcategory, SKU} = getFxPageContext(page.pathname)
    const source =
        el.closest('[data-fx-source]')?.dataset.fxSource ||
        el.closest('header, nav, footer, aside, form, section, main')?.tagName.toLowerCase() ||
        ''
    const props = {
        id: ctaLabel(el),
        source,
        Category,
        Subcategory,
        SKU,
        URL: pageUrl,
        state: ctaState(el),
        class: typeof el.className === 'string' ? el.className.slice(0, 120) : ''
    }
    // One event for every CTA; metrics filter on the `id` property.
    trackFxEvent('cta_click', null, props)

    const href = el.tagName === 'A' ? el.href : ''
    if (!href || /^(javascript|mailto|tel):/i.test(href)) return
    let target
    try {
        target = new URL(href, window.location.href)
    } catch {
        return
    }
    if (target.host === window.location.host) return
    sendNavigation({
        from: page.context.page_type,
        to: 'external',
        navType: 'exit',
        fromUrl: window.location.href,
        toUrl: target.href,
        external: true
    })
    sendAbandonCheckout(page, 'external')
}

const onPageHide = () => {
    if (!page) return
    pauseActive()
    sendTimeOnPage()
    page = null
}

/**
 * Start the document listeners once. Safe to call repeatedly; no-op on SSR.
 * @param {{siteCountry: string}} options
 */
export const startFxBehaviourTracking = ({siteCountry: country}) => {
    siteCountry = country || ''
    if (started || typeof window === 'undefined') return
    started = true
    const passive = {passive: true}
    ACTIVITY_EVENTS.forEach((type) => document.addEventListener(type, onActivity, passive))
    document.addEventListener('visibilitychange', onVisibility)
    document.addEventListener('click', onClick, {capture: true, passive: true})
    window.addEventListener('scroll', onScroll, passive)
    window.addEventListener('pagehide', onPageHide)
}

/**
 * A page view began (hard load or route change). Closes the previous page
 * (time_on_page + count, abandon_checkout) and opens the new one (navigation).
 * @param {string} pathname router pathname, locale prefix included
 */
export const fxPageView = (pathname) => {
    if (!started || typeof window === 'undefined') return
    const previous = page
    if (previous) {
        pauseActive()
        sendTimeOnPage()
    }

    page = {
        pathname,
        context: getFxPageContext(pathname),
        activeMs: 0,
        activeSince: null,
        scrollMarks: new Set(),
        contentState: 'initial_load'
    }
    onActivity()

    const to = page.context.page_type
    if (previous) {
        sendAbandonCheckout(previous, to)
        sendNavigation({
            from: previous.context.page_type,
            to,
            navType: 'internal',
            fromUrl: previous.context.URL,
            toUrl: window.location.href
        })
    } else {
        let referrer = null
        try {
            referrer = document.referrer ? new URL(document.referrer) : null
        } catch {
            referrer = null
        }
        const internal = referrer && referrer.host === window.location.host
        let from = 'direct'
        if (internal) from = classifyPage(referrer.pathname)
        else if (referrer) from = 'external'
        sendNavigation({
            from,
            to,
            navType: internal ? 'internal' : 'entry',
            fromUrl: internal ? referrer.href : referrer ? stripQuery(referrer.href) : '',
            toUrl: window.location.href,
            external: false
        })
    }
    onScroll()
}

/** The page expanded content (PLP "Show more"); later scroll_depth events say so. */
export const markFxContentExpanded = () => {
    if (page) page.contentState = 'expanded_content'
}
